import { Component, lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import type { GalleryImage } from "../../lib/portfolios";
import { useCanUseWebgl } from "../../lib/can-use-webgl";
import { OPENING_TITLE_END, openingFrame } from "./motion";
import { useScrollScene } from "./use-scroll-scene";
import "./portfolio.css";

const Scene = lazy(() => import("./PortfolioScene.client"));

class SceneBoundary extends Component<{ children: ReactNode; onFailure: () => void }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { this.props.onFailure(); }
  render() { return this.state.failed ? null : this.props.children; }
}

function useSceneStatus() {
  const enabled = useCanUseWebgl();
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const onReady = useCallback(() => setReady(true), []);
  const onFailure = useCallback(() => setFailed(true), []);
  return { enabled: enabled && !failed, ready: enabled && ready && !failed, onReady, onFailure };
}

export function ScrollOpening({ images }: { images: GalleryImage[] }) {
  const title = useRef<HTMLHeadingElement>(null);
  const titleProgress = useRef(-1);
  const photos = useRef<(HTMLImageElement | null)[]>([]);
  const status = useSceneStatus();
  const updateTitle = useCallback((p: number) => {
    if (title.current) {
      const handoff = Math.min(1, p / OPENING_TITLE_END);
      if (handoff === titleProgress.current) return;
      titleProgress.current = handoff;
      title.current.style.transform = `translateY(${-handoff * 36}px) scale(${1 - handoff * 0.18})`;
      title.current.style.opacity = String(1 - handoff);
    }
  }, []);
  const scene = useScrollScene((p, stage, measure) => {
    if (status.ready) return;
    updateTitle(p);
    const width = stage.clientWidth;
    const height = stage.clientHeight;
    photos.current.forEach((photo, index) => {
      if (!photo) return;
      const base = openingFrame(index, 0, width, height, images[index].ratio);
      const f = openingFrame(index, p, width, height, images[index].ratio);
      if (measure) {
        photo.style.left = "0";
        photo.style.top = "0";
        photo.style.width = `${base.width}px`;
        photo.style.height = `${base.height}px`;
      }
      photo.style.transform = `translate3d(${f.x - f.width / 2}px, ${f.y - f.height / 2}px, 0) scale(${f.width / Math.max(1, base.width)})`;
    });
  });
  useEffect(scene.refresh, [scene.refresh, status.ready]);

  return (
    <section ref={scene.root} className="scroll-opening" aria-label="Photography by Mari">
      <div ref={scene.stage} className="portfolio-stage opening-stage">
        <h1 ref={title} className="photographer-name"><span>Captures</span><span>by Mari</span></h1>
        <p className="opening-location">Photographer<br />Kern County, California</p>
        <div className={`opening-photographs ${status.ready ? "scene-ready" : ""}`}>
          {images.map((photo, index) => <img key={photo.src} ref={(node) => { photos.current[index] = node; }} src={photo.src} alt={photo.alt} className={`opening-photo opening-photo-${index}`} fetchPriority={index === 0 ? "high" : "auto"} />)}
        </div>
        {status.enabled && (scene.active || status.ready) && <div className="portfolio-canvas" aria-hidden>
          <SceneBoundary onFailure={status.onFailure}><Suspense fallback={null}><Scene variant="opening" images={images} progress={scene.progress} active={scene.active} onReady={status.onReady} onFailure={status.onFailure} onProgress={updateTitle} /></Suspense></SceneBoundary>
        </div>}
        <div className="opening-bottom"><span>Soccer & track photographs</span><a href="#selected-photographs">Scroll to view ↓</a></div>
      </div>
    </section>
  );
}

export function HorizontalSequence({ images, title = "Selected photographs", id = "selected-photographs" }: { images: GalleryImage[]; title?: string; id?: string }) {
  const track = useRef<HTMLDivElement>(null);
  const measurements = useRef({ travel: 0, width: 0, centers: [] as number[] });
  const [current, setCurrent] = useState(0);
  const scene = useScrollScene((p, stage, measure) => {
    const rail = track.current;
    if (!rail) return;
    if (measure) {
      const frames = Array.from(rail.children) as HTMLElement[];
      if (!frames.length) return;
      const width = stage.clientWidth;
      const firstWidth = frames[0].offsetWidth;
      const lastWidth = frames[frames.length - 1].offsetWidth;
      rail.style.paddingLeft = `${Math.max(24, (width - firstWidth) / 2)}px`;
      rail.style.paddingRight = `${Math.max(24, (width - lastWidth) / 2)}px`;
      measurements.current = { width, travel: Math.max(0, rail.scrollWidth - width), centers: frames.map(frame => frame.offsetLeft + frame.offsetWidth / 2) };
    }
    const { travel, width, centers } = measurements.current;
    rail.style.transform = `translate3d(${-p * travel}px, 0, 0)`;
    const center = p * travel + width / 2;
    const nearest = centers.reduce((best, position, index) => Math.abs(position - center) < Math.abs(centers[best] - center) ? index : best, 0);
    setCurrent(nearest);
  });
  useEffect(() => {
    const observer = new ResizeObserver(scene.refresh);
    if (track.current) observer.observe(track.current);
    return () => observer.disconnect();
  }, [scene.refresh]);

  function jumpToPhoto(index: number) {
    const { travel, width, centers } = measurements.current;
    if (centers[index] === undefined) return;
    scene.goTo((centers[index] - width / 2) / Math.max(1, travel));
  }

  return (
    <section ref={scene.root} id={id} className="horizontal-sequence" style={{ height: `${Math.max(180, images.length * 42)}svh` }} aria-label={title}>
      <div ref={scene.stage} className="portfolio-stage sequence-stage">
        <header className="sequence-heading"><h2>{title}</h2><span>{String(current + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}</span></header>
        <div className="sequence-window">
          <div ref={track} className="sequence-track">
            {images.map((photo, index) => <figure key={photo.src} id={`${id}-${index}`} className={`sequence-photo sequence-photo-${index % 3}`}>
              <img src={photo.src} alt={photo.alt} loading="lazy" style={{ aspectRatio: photo.ratio }} />
              <figcaption><span>{photo.alt}</span><span>{String(index + 1).padStart(2, "0")}</span></figcaption>
            </figure>)}
          </div>
        </div>
        <nav className="photo-minimap" aria-label="Jump to a photograph">
          {images.map((photo, index) => <button key={photo.src} type="button" aria-label={`View photograph ${index + 1}: ${photo.alt}`} aria-pressed={index === current} onClick={() => jumpToPhoto(index)}><img src={photo.src} alt="" loading="lazy" /></button>)}
        </nav>
      </div>
    </section>
  );
}

export function Panorama({ images }: { images: GalleryImage[] }) {
  const track = useRef<HTMLDivElement>(null);
  const travel = useRef(0);
  const [current, setCurrent] = useState(0);
  const status = useSceneStatus();
  const scene = useScrollScene((p, stage, measure) => {
    setCurrent(Math.round(p * (images.length - 1)));
    if (status.ready || !track.current) return;
    if (measure) travel.current = Math.max(0, track.current.scrollWidth - stage.clientWidth);
    track.current.style.transform = `translate3d(${-p * travel.current}px, 0, 0)`;
  });
  useEffect(scene.refresh, [scene.refresh, status.ready]);
  useEffect(() => {
    const observer = new ResizeObserver(scene.refresh);
    if (track.current) observer.observe(track.current);
    return () => observer.disconnect();
  }, [scene.refresh]);

  return (
    <section ref={scene.root} className="panorama-section" aria-label="Photograph panorama">
      <div ref={scene.stage} className="portfolio-stage panorama-stage">
        <header className="panorama-heading"><h2>Photographs</h2><Link to="/work">Explore the work ↗</Link></header>
        <div className={`panorama-fallback ${status.ready ? "scene-ready" : ""}`}><div ref={track}>{images.map((photo) => <img key={photo.src} src={photo.src} alt={photo.alt} loading="lazy" />)}</div></div>
        {status.enabled && (scene.active || status.ready) && <div className="portfolio-canvas panorama-canvas" aria-hidden><SceneBoundary onFailure={status.onFailure}><Suspense fallback={null}><Scene variant="panorama" images={images} progress={scene.progress} active={scene.active} onReady={status.onReady} onFailure={status.onFailure} /></Suspense></SceneBoundary></div>}
        <div className="panorama-caption"><span>{images[current]?.alt}</span><span>{String(current + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}</span></div>
      </div>
    </section>
  );
}
